import * as React from 'react';
import { Archive, Play, Info, Pencil, ListChecks, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  DashboardTableStatusTone,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { formatCurrency, formatDisplayDate } from '../../../../../lib/format';
import { readShigoAppConfig } from '../../../../../lib/config';
import { ShigoNetwork } from '../../../../../lib/deployment';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import {
  useDomainComplianceMutations,
  usePublisherDomainsQuery,
  usePublisherDomainStatusCountsQuery,
} from '../../hooks';
import {
  domainsPageSizes,
  PublisherDomainsAddMethod,
  PublisherDomainStatus,
  type PublisherDomainListItem,
} from '../../model';
import { usePublisherDomainsRouteFilters } from '../../usePublisherDomainsRouteFilters';
import { PublisherCsvManageDialog } from '../../../publishers/components/PublisherCsvManageDialog';
import { PublisherDomainDialog } from '../PublisherDomainsDialogs';

const statusOptions = Object.values(PublisherDomainStatus);

export function PublisherDomainsTable({ publisherId }: { publisherId: string }) {
  const { can } = useShigoPermissions();
  const routeFilters = usePublisherDomainsRouteFilters();
  const { filter } = routeFilters;
  const query = usePublisherDomainsQuery(publisherId, filter);
  const counts = usePublisherDomainStatusCountsQuery(publisherId, filter.searchTerm);
  const mutations = useDomainComplianceMutations();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [addMethod, setAddMethod] = React.useState<PublisherDomainsAddMethod | null>(null);
  const [manageOpen, setManageOpen] = React.useState(false);
  const [editingDomain, setEditingDomain] = React.useState<PublisherDomainListItem | null>(null);
  // Explicit legacy tenant exception: Triton CPM is Kiwi-only, not an IMMU feature.
  const showTriton = readShigoAppConfig().network === ShigoNetwork.Kiwi;
  const rows = query.data?.content ?? [];
  const canCreate = can(ShigoPermission.SupplyPublishersDomainsCreate);
  const canManage = can(ShigoPermission.SupplyPublishersDomainsUpdate);
  async function toggleStatus(domain: PublisherDomainListItem) {
    if (mutations.setPublisherDomainActive.isPending) return;
    const active = domain.status === PublisherDomainStatus.Archived;
    try {
      await mutations.setPublisherDomainActive.mutateAsync({
        publisherId,
        domainId: domain.domainId,
        active,
      });
      toast.success(
        dashboardT(
          active
            ? 'publisherDetails.domains.activateSuccess'
            : 'publisherDetails.domains.archiveSuccess',
        ),
      );
    } catch (error) {
      showRequestError(error, dashboardT('publisherDetails.domains.statusError'));
    }
  }
  const selectedStatuses = new Set(filter.statuses);
  const statusLabel = filter.statuses.length
    ? filter.statuses.map(publisherDomainStatusLabel).join(', ')
    : dashboardT('supplyDomains.allStatuses');
  const columns = buildDashboardDataTableColumns<PublisherDomainListItem>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyDomains.domainName'),
      headerClassName: showTriton ? 'w-[24%] pl-4' : 'w-[30%] pl-4',
      className: 'pl-4 font-medium',
      value: (domain) => domain.url,
    },
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('supplyDomains.status'),
      headerClassName: showTriton ? 'w-[10%]' : 'w-[14%]',
      label: (domain) => publisherDomainStatusLabel(domain.status),
      tone: (domain) =>
        domain.status === PublisherDomainStatus.Active
          ? DashboardTableStatusTone.Success
          : DashboardTableStatusTone.Neutral,
    },
    {
      id: 'createdBy',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyDomains.createdBy'),
      headerClassName: showTriton ? 'w-[16%]' : 'w-[20%]',
      value: (domain) => domain.createdBy,
    },
    {
      id: 'createdOn',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('supplyDomains.created'),
      headerClassName: showTriton ? 'w-[12%]' : 'w-[16%]',
      value: (domain) => formatDisplayDate(domain.createdOn),
    },
    {
      id: 'updatedOn',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('supplyDomains.updated'),
      headerClassName: showTriton ? 'w-[12%]' : 'w-[16%]',
      value: (domain) => formatDisplayDate(domain.updatedOn),
    },
    ...(showTriton
      ? [
          {
            id: 'tritonCpm',
            kind: DashboardDataTableColumnKind.Text as const,
            header: dashboardT('supplyDomains.triton.today'),
            headerClassName: 'w-[12%]',
            value: (domain: PublisherDomainListItem) => formatCurrency(domain.lastTritonCpm),
          },
          {
            id: 'tritonHistory',
            kind: DashboardDataTableColumnKind.Custom as const,
            header: dashboardT('supplyDomains.triton.history'),
            headerClassName: 'w-[10%]',
            cell: (domain: PublisherDomainListItem) =>
              domain.last7DaysTritonCpm.length ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={dashboardT('supplyDomains.triton.history')}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1 tabular-nums">
                      {domain.last7DaysTritonCpm.map((day) => (
                        <div key={day.date} className="flex justify-between gap-5">
                          <span>{day.date}</span>
                          <span>{formatCurrency(day.tritonCpm)}</span>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                '-'
              ),
          },
        ]
      : []),
    ...(canManage
      ? [
          {
            id: 'actions',
            kind: DashboardDataTableColumnKind.Actions as const,
            cell: (domain: PublisherDomainListItem) => (
              <DashboardTableRowActions
                label={dashboardT('publisherDetails.domains.actions')}
                disabled={mutations.setPublisherDomainActive.isPending}
              >
                <DashboardTableRowActionItem
                  onSelect={() => {
                    setEditingDomain(domain);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  {dashboardT('publisherDetails.domains.edit')}
                </DashboardTableRowActionItem>
                <DashboardTableRowActionItem
                  onSelect={() => {
                    void toggleStatus(domain);
                  }}
                >
                  {domain.status === PublisherDomainStatus.Active ? (
                    <Archive className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {dashboardT(
                    domain.status === PublisherDomainStatus.Active
                      ? 'publisherDetails.domains.archive'
                      : 'publisherDetails.domains.activate',
                  )}
                </DashboardTableRowActionItem>
              </DashboardTableRowActions>
            ),
          },
        ]
      : []),
  ]);

  return (
    <>
      {editingDomain && canManage ? (
        <PublisherDomainDialog
          publisherId={publisherId}
          method={PublisherDomainsAddMethod.Single}
          domain={editingDomain}
          onClose={() => {
            setEditingDomain(null);
          }}
        />
      ) : null}
      <DashboardDataTable
        title={dashboardT('supplyDomains.title')}
        tableLabel={dashboardT('supplyDomains.title')}
        minWidthClassName={showTriton ? 'min-w-[1000px]' : 'min-w-[720px]'}
        columns={columns}
        items={rows}
        getRowKey={(domain) => domain.domainId}
        loading={query.isLoading}
        loadingMessage={dashboardT('supplyDomains.loading')}
        errorMessage={query.isError ? dashboardT('supplyDomains.loadError') : undefined}
        emptyMessage={dashboardT('supplyDomains.empty')}
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('supplyDomains.search')}
              placeholder={dashboardT('supplyDomains.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <DashboardTableFilterTrigger
                  label={statusLabel}
                  aria-label={dashboardT('supplyDomains.statusFilter')}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {statusOptions.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onSelect={(event) => {
                      event.preventDefault();
                    }}
                    onCheckedChange={(checked) => {
                      routeFilters.setStatuses(
                        checked
                          ? Array.from(new Set([...filter.statuses, status]))
                          : filter.statuses.filter((item) => item !== status),
                      );
                    }}
                  >
                    <span className="flex min-w-0 flex-1 justify-between gap-3">
                      <span>{publisherDomainStatusLabel(status)}</span>
                      {counts.data ? (
                        <span className="text-muted-foreground">{counts.data[status] ?? 0}</span>
                      ) : null}
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canManage && rows.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setManageOpen(true);
                }}
              >
                <ListChecks className="h-4 w-4" aria-hidden="true" />
                {dashboardT('publisherDetails.domains.manage')}
              </Button>
            ) : null}
            {canCreate ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button">
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('publisherDetails.domains.add')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {Object.values(PublisherDomainsAddMethod).map((method) => (
                    <DropdownMenuItem
                      key={method}
                      onSelect={() => {
                        setAddMethod(method);
                      }}
                    >
                      {dashboardT(
                        method === PublisherDomainsAddMethod.Single
                          ? 'publisherDetails.domains.single'
                          : 'publisherDetails.domains.multiple',
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: domainsPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            routeFilters.setPagination(next.page, next.pageSize);
          },
        }}
      />
      {addMethod ? (
        <PublisherDomainDialog
          publisherId={publisherId}
          method={addMethod}
          onClose={() => {
            setAddMethod(null);
          }}
        />
      ) : null}
      {manageOpen ? (
        <PublisherCsvManageDialog
          kind="domains"
          filename="publisher-domains.csv"
          downloadPending={mutations.exportPublisherDomains.isPending}
          uploadPending={mutations.reimportPublisherDomains.isPending}
          onDownload={() => mutations.exportPublisherDomains.mutateAsync(publisherId)}
          onUpload={(file) => mutations.reimportPublisherDomains.mutateAsync({ publisherId, file })}
          onClose={() => {
            setManageOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function publisherDomainStatusLabel(status: PublisherDomainStatus) {
  return dashboardT(
    status === PublisherDomainStatus.Active
      ? 'supplyDomains.status.active'
      : 'supplyDomains.status.archived',
  );
}
